import { isNilOrEmpty } from 'helpers';

export interface IRegion {
  name: string;
  abbreviation: string;
}

export interface ICountry {
  name: string;
  abbreviation: string;
  regions: IRegion[];
}

export interface CountryCodes {
  code: string;
  country: string;
}

export const SUPPORTED_COUNTRY_CODES = {
  UK: 'GB',
  US: 'US',
  CA: 'CA',
  ES: 'ES',
  IT: 'IT',
  BE: 'BE',
  DE: 'DE',
  FR: 'FR',
}

// Countries with states/provinces: state is required.
// Mirrors Address::LOCAL_COUNTRIES on the backend.
export const LOCAL_COUNTRY_CODES = [
  SUPPORTED_COUNTRY_CODES.US,
  SUPPORTED_COUNTRY_CODES.CA,
];

// Countries without states: postal code is required instead, and phone numbers
// need a country code. Mirrors Address::INTERNATIONAL_COUNTRIES on the backend.
export const INTERNATIONAL_COUNTRY_CODES = [
  SUPPORTED_COUNTRY_CODES.UK,
  SUPPORTED_COUNTRY_CODES.ES,
  SUPPORTED_COUNTRY_CODES.IT,
  SUPPORTED_COUNTRY_CODES.BE,
  SUPPORTED_COUNTRY_CODES.DE,
  SUPPORTED_COUNTRY_CODES.FR,
];

export function isInternationalCountry(countryCode: string): boolean {
  return INTERNATIONAL_COUNTRY_CODES.includes(countryCode?.toUpperCase());
}

// Example local phone number formats, used as input placeholders
export const PHONE_PLACEHOLDERS = {
  [SUPPORTED_COUNTRY_CODES.US]: 'e.g. (555) 555-5555',
  [SUPPORTED_COUNTRY_CODES.CA]: 'e.g. (555) 555-5555',
  [SUPPORTED_COUNTRY_CODES.UK]: 'e.g. 020 1234 5678',
  [SUPPORTED_COUNTRY_CODES.ES]: 'e.g. 612 34 56 78',
  [SUPPORTED_COUNTRY_CODES.IT]: 'e.g. 312 345 6789',
  [SUPPORTED_COUNTRY_CODES.BE]: 'e.g. 0470 12 34 56',
  [SUPPORTED_COUNTRY_CODES.DE]: 'e.g. 030 123456',
  [SUPPORTED_COUNTRY_CODES.FR]: 'e.g. 06 12 34 56 78',
};

export function getPhonePlaceholder(countryCode: string): string {
  return PHONE_PLACEHOLDERS[countryCode?.toUpperCase()] || PHONE_PLACEHOLDERS[SUPPORTED_COUNTRY_CODES.US];
}

// https://github.com/google/libphonenumber/blob/master/FAQ.md#why-are-bouvet-island-bv-pitcairn-island-pn-antarctica-aq-etc-not-supported
export const ISO_3166_1_CODES: CountryCodes[] = [
  { code: 'AF', country: 'Afghanistan' },
  { code: 'AX', country: 'Åland Islands' },
  { code: 'AL', country: 'Albania' },
  { code: 'DZ', country: 'Algeria' },
  { code: 'AS', country: 'American Samoa' },
  { code: 'AD', country: 'Andorra' },
  { code: 'AO', country: 'Angola' },
  { code: 'AI', country: 'Anguilla' },
  // {code: 'AQ', country: 'Antarctica'},
  { code: 'AG', country: 'Antigua and Barbuda' },
  { code: 'AR', country: 'Argentina' },
  { code: 'AM', country: 'Armenia' },
  { code: 'AW', country: 'Aruba' },
  { code: 'AU', country: 'Australia' },
  { code: 'AT', country: 'Austria' },
  { code: 'AZ', country: 'Azerbaijan' },
  { code: 'BS', country: 'Bahamas' },
  { code: 'BH', country: 'Bahrain' },
  { code: 'BD', country: 'Bangladesh' },
  { code: 'BB', country: 'Barbados' },
  { code: 'BY', country: 'Belarus' },
  { code: 'BE', country: 'Belgium' },
  { code: 'BZ', country: 'Belize' },
  { code: 'BJ', country: 'Benin' },
  { code: 'BM', country: 'Bermuda' },
  { code: 'BT', country: 'Bhutan' },
  { code: 'BO', country: 'Bolivia (Plurinational State of)' },
  { code: 'BQ', country: 'Bonaire, Sint Eustatius and Saba' },
  { code: 'BA', country: 'Bosnia and Herzegovina' },
  { code: 'BW', country: 'Botswana' },
  // {code: 'BV', country: 'Bouvet Island'},
  { code: 'BR', country: 'Brazil' },
  { code: 'IO', country: 'British Indian Ocean Territory' },
  { code: 'BN', country: 'Brunei Darussalam' },
  { code: 'BG', country: 'Bulgaria' },
  { code: 'BF', country: 'Burkina Faso' },
  { code: 'BI', country: 'Burundi' },
  { code: 'CV', country: 'Cabo Verde' },
  { code: 'KH', country: 'Cambodia' },
  { code: 'CM', country: 'Cameroon' },
  { code: 'CA', country: 'Canada' },
  { code: 'KY', country: 'Cayman Islands' },
  { code: 'CF', country: 'Central African Republic' },
  { code: 'TD', country: 'Chad' },
  { code: 'CL', country: 'Chile' },
  { code: 'CN', country: 'China' },
  { code: 'CX', country: 'Christmas Island' },
  { code: 'CC', country: 'Cocos (Keeling) Islands' },
  { code: 'CO', country: 'Colombia' },
  { code: 'KM', country: 'Comoros' },
  { code: 'CG', country: 'Congo' },
  { code: 'CD', country: 'Congo, Democratic Republic of the' },
  { code: 'CK', country: 'Cook Islands' },
  { code: 'CR', country: 'Costa Rica' },
  { code: 'CI', country: `Côte d'Ivoire` },
  { code: 'HR', country: 'Croatia' },
  { code: 'CU', country: 'Cuba' },
  { code: 'CW', country: 'Curaçao' },
  { code: 'CY', country: 'Cyprus' },
  { code: 'CZ', country: 'Czechia' },
  { code: 'DK', country: 'Denmark' },
  { code: 'DJ', country: 'Djibouti' },
  { code: 'DM', country: 'Dominica' },
  { code: 'DO', country: 'Dominican Republic' },
  { code: 'EC', country: 'Ecuador' },
  { code: 'EG', country: 'Egypt' },
  { code: 'SV', country: 'El Salvador' },
  { code: 'GQ', country: 'Equatorial Guinea' },
  { code: 'ER', country: 'Eritrea' },
  { code: 'EE', country: 'Estonia' },
  { code: 'SZ', country: 'Eswatini' },
  { code: 'ET', country: 'Ethiopia' },
  { code: 'FK', country: 'Falkland Islands (Malvinas)' },
  { code: 'FO', country: 'Faroe Islands' },
  { code: 'FJ', country: 'Fiji' },
  { code: 'FI', country: 'Finland' },
  { code: 'FR', country: 'France' },
  { code: 'GF', country: 'French Guiana' },
  { code: 'PF', country: 'French Polynesia' },
  // {code: 'TF', country: 'French Southern Territories'},
  { code: 'GA', country: 'Gabon' },
  { code: 'GM', country: 'Gambia' },
  { code: 'GE', country: 'Georgia' },
  { code: 'DE', country: 'Germany' },
  { code: 'GH', country: 'Ghana' },
  { code: 'GI', country: 'Gibraltar' },
  { code: 'GR', country: 'Greece' },
  { code: 'GL', country: 'Greenland' },
  { code: 'GD', country: 'Grenada' },
  { code: 'GP', country: 'Guadeloupe' },
  { code: 'GU', country: 'Guam' },
  { code: 'GT', country: 'Guatemala' },
  { code: 'GG', country: 'Guernsey' },
  { code: 'GN', country: 'Guinea' },
  { code: 'GW', country: 'Guinea-Bissau' },
  { code: 'GY', country: 'Guyana' },
  { code: 'HT', country: 'Haiti' },
  // {code: 'HM', country: 'Heard Island and McDonald Islands'},
  { code: 'VA', country: 'Holy See' },
  { code: 'HN', country: 'Honduras' },
  { code: 'HK', country: 'Hong Kong' },
  { code: 'HU', country: 'Hungary' },
  { code: 'IS', country: 'Iceland' },
  { code: 'IN', country: 'India' },
  { code: 'ID', country: 'Indonesia' },
  { code: 'IR', country: 'Iran (Islamic Republic of)' },
  { code: 'IQ', country: 'Iraq' },
  { code: 'IE', country: 'Ireland' },
  { code: 'IM', country: 'Isle of Man' },
  { code: 'IL', country: 'Israel' },
  { code: 'IT', country: 'Italy' },
  { code: 'JM', country: 'Jamaica' },
  { code: 'JP', country: 'Japan' },
  { code: 'JE', country: 'Jersey' },
  { code: 'JO', country: 'Jordan' },
  { code: 'KZ', country: 'Kazakhstan' },
  { code: 'KE', country: 'Kenya' },
  { code: 'KI', country: 'Kiribati' },
  { code: 'KP', country: 'Korea (Democratic People\'s Republic of)' },
  { code: 'KR', country: 'Korea, Republic of' },
  { code: 'KW', country: 'Kuwait' },
  { code: 'KG', country: 'Kyrgyzstan' },
  { code: 'LA', country: 'Lao People\'s Democratic Republic' },
  { code: 'LV', country: 'Latvia' },
  { code: 'LB', country: 'Lebanon' },
  { code: 'LS', country: 'Lesotho' },
  { code: 'LR', country: 'Liberia' },
  { code: 'LY', country: 'Libya' },
  { code: 'LI', country: 'Liechtenstein' },
  { code: 'LT', country: 'Lithuania' },
  { code: 'LU', country: 'Luxembourg' },
  { code: 'MO', country: 'Macao' },
  { code: 'MG', country: 'Madagascar' },
  { code: 'MW', country: 'Malawi' },
  { code: 'MY', country: 'Malaysia' },
  { code: 'MV', country: 'Maldives' },
  { code: 'ML', country: 'Mali' },
  { code: 'MT', country: 'Malta' },
  { code: 'MH', country: 'Marshall Islands' },
  { code: 'MQ', country: 'Martinique' },
  { code: 'MR', country: 'Mauritania' },
  { code: 'MU', country: 'Mauritius' },
  { code: 'YT', country: 'Mayotte' },
  { code: 'MX', country: 'Mexico' },
  { code: 'FM', country: 'Micronesia (Federated States of)' },
  { code: 'MD', country: 'Moldova, Republic of' },
  { code: 'MC', country: 'Monaco' },
  { code: 'MN', country: 'Mongolia' },
  { code: 'ME', country: 'Montenegro' },
  { code: 'MS', country: 'Montserrat' },
  { code: 'MA', country: 'Morocco' },
  { code: 'MZ', country: 'Mozambique' },
  { code: 'MM', country: 'Myanmar' },
  { code: 'NA', country: 'Namibia' },
  { code: 'NR', country: 'Nauru' },
  { code: 'NP', country: 'Nepal' },
  { code: 'NL', country: 'Netherlands' },
  { code: 'NC', country: 'New Caledonia' },
  { code: 'NZ', country: 'New Zealand' },
  { code: 'NI', country: 'Nicaragua' },
  { code: 'NE', country: 'Niger' },
  { code: 'NG', country: 'Nigeria' },
  { code: 'NU', country: 'Niue' },
  { code: 'NF', country: 'Norfolk Island' },
  { code: 'MK', country: 'North Macedonia' },
  { code: 'MP', country: 'Northern Mariana Islands' },
  { code: 'NO', country: 'Norway' },
  { code: 'OM', country: 'Oman' },
  { code: 'PK', country: 'Pakistan' },
  { code: 'PW', country: 'Palau' },
  { code: 'PS', country: 'Palestine, State of' },
  { code: 'PA', country: 'Panama' },
  { code: 'PG', country: 'Papua New Guinea' },
  { code: 'PY', country: 'Paraguay' },
  { code: 'PE', country: 'Peru' },
  { code: 'PH', country: 'Philippines' },
  // {code: 'PN', country: 'Pitcairn'},
  { code: 'PL', country: 'Poland' },
  { code: 'PT', country: 'Portugal' },
  { code: 'PR', country: 'Puerto Rico' },
  { code: 'QA', country: 'Qatar' },
  { code: 'RE', country: 'Réunion' },
  { code: 'RO', country: 'Romania' },
  { code: 'RU', country: 'Russian Federation' },
  { code: 'RW', country: 'Rwanda' },
  { code: 'BL', country: 'Saint Barthélemy' },
  { code: 'SH', country: 'Saint Helena, Ascension and Tristan da Cunha' },
  { code: 'KN', country: 'Saint Kitts and Nevis' },
  { code: 'LC', country: 'Saint Lucia' },
  { code: 'MF', country: 'Saint Martin (French part)' },
  { code: 'PM', country: 'Saint Pierre and Miquelon' },
  { code: 'VC', country: 'Saint Vincent and the Grenadines' },
  { code: 'WS', country: 'Samoa' },
  { code: 'SM', country: 'San Marino' },
  { code: 'ST', country: 'Sao Tome and Principe' },
  { code: 'SA', country: 'Saudi Arabia' },
  { code: 'SN', country: 'Senegal' },
  { code: 'RS', country: 'Serbia' },
  { code: 'SC', country: 'Seychelles' },
  { code: 'SL', country: 'Sierra Leone' },
  { code: 'SG', country: 'Singapore' },
  { code: 'SX', country: 'Sint Maarten (Dutch part)' },
  { code: 'SK', country: 'Slovakia' },
  { code: 'SI', country: 'Slovenia' },
  { code: 'SB', country: 'Solomon Islands' },
  { code: 'SO', country: 'Somalia' },
  { code: 'ZA', country: 'South Africa' },
  // {code: 'GS', country: 'South Georgia and the South Sandwich Islands'},
  { code: 'SS', country: 'South Sudan' },
  { code: 'ES', country: 'Spain' },
  { code: 'LK', country: 'Sri Lanka' },
  { code: 'SD', country: 'Sudan' },
  { code: 'SR', country: 'Suriname' },
  { code: 'SJ', country: 'Svalbard and Jan Mayen' },
  { code: 'SE', country: 'Sweden' },
  { code: 'CH', country: 'Switzerland' },
  { code: 'SY', country: 'Syrian Arab Republic' },
  { code: 'TW', country: 'Taiwan, Province of China' },
  { code: 'TJ', country: 'Tajikistan' },
  { code: 'TZ', country: 'Tanzania, United Republic of' },
  { code: 'TH', country: 'Thailand' },
  { code: 'TL', country: 'Timor-Leste' },
  { code: 'TG', country: 'Togo' },
  { code: 'TK', country: 'Tokelau' },
  { code: 'TO', country: 'Tonga' },
  { code: 'TT', country: 'Trinidad and Tobago' },
  { code: 'TN', country: 'Tunisia' },
  { code: 'TR', country: 'Turkey' },
  { code: 'TM', country: 'Turkmenistan' },
  { code: 'TC', country: 'Turks and Caicos Islands' },
  { code: 'TV', country: 'Tuvalu' },
  { code: 'UG', country: 'Uganda' },
  { code: 'UA', country: 'Ukraine' },
  { code: 'AE', country: 'United Arab Emirates' },
  { code: 'GB', country: 'United Kingdom of Great Britain and Northern Ireland' },
  { code: 'US', country: 'United States of America' },
  // {code: 'UM', country: 'United States Minor Outlying Islands'},
  { code: 'UY', country: 'Uruguay' },
  { code: 'UZ', country: 'Uzbekistan' },
  { code: 'VU', country: 'Vanuatu' },
  { code: 'VE', country: 'Venezuela (Bolivarian Republic of)' },
  { code: 'VN', country: 'Viet Nam' },
  { code: 'VG', country: 'Virgin Islands (British)' },
  { code: 'VI', country: 'Virgin Islands (U.S.)' },
  { code: 'WF', country: 'Wallis and Futuna' },
  { code: 'EH', country: 'Western Sahara' },
  { code: 'YE', country: 'Yemen' },
  { code: 'ZM', country: 'Zambia' },
  { code: 'ZW', country: 'Zimbabwe' }
];

export const Countries: ICountry[] = [
  {
    name: 'United States of America',
    abbreviation: 'US',
    regions: [
      {
        name: 'Alabama',
        abbreviation: 'AL'
      },
      {
        name: 'Alaska',
        abbreviation: 'AK'
      },
      {
        name: 'American Samoa',
        abbreviation: 'AS'
      },
      {
        name: 'Arizona',
        abbreviation: 'AZ'
      },
      {
        name: 'Arkansas',
        abbreviation: 'AR'
      },
      {
        name: 'California',
        abbreviation: 'CA'
      },
      {
        name: 'Colorado',
        abbreviation: 'CO'
      },
      {
        name: 'Connecticut',
        abbreviation: 'CT'
      },
      {
        name: 'Delaware',
        abbreviation: 'DE'
      },
      {
        name: 'District Of Columbia',
        abbreviation: 'DC'
      },
      {
        name: 'Federated States Of Micronesia',
        abbreviation: 'FM'
      },
      {
        name: 'Florida',
        abbreviation: 'FL'
      },
      {
        name: 'Georgia',
        abbreviation: 'GA'
      },
      {
        name: 'Guam',
        abbreviation: 'GU'
      },
      {
        name: 'Hawaii',
        abbreviation: 'HI'
      },
      {
        name: 'Idaho',
        abbreviation: 'ID'
      },
      {
        name: 'Illinois',
        abbreviation: 'IL'
      },
      {
        name: 'Indiana',
        abbreviation: 'IN'
      },
      {
        name: 'Iowa',
        abbreviation: 'IA'
      },
      {
        name: 'Kansas',
        abbreviation: 'KS'
      },
      {
        name: 'Kentucky',
        abbreviation: 'KY'
      },
      {
        name: 'Louisiana',
        abbreviation: 'LA'
      },
      {
        name: 'Maine',
        abbreviation: 'ME'
      },
      {
        name: 'Marshall Islands',
        abbreviation: 'MH'
      },
      {
        name: 'Maryland',
        abbreviation: 'MD'
      },
      {
        name: 'Massachusetts',
        abbreviation: 'MA'
      },
      {
        name: 'Michigan',
        abbreviation: 'MI'
      },
      {
        name: 'Minnesota',
        abbreviation: 'MN'
      },
      {
        name: 'Mississippi',
        abbreviation: 'MS'
      },
      {
        name: 'Missouri',
        abbreviation: 'MO'
      },
      {
        name: 'Montana',
        abbreviation: 'MT'
      },
      {
        name: 'Nebraska',
        abbreviation: 'NE'
      },
      {
        name: 'Nevada',
        abbreviation: 'NV'
      },
      {
        name: 'New Hampshire',
        abbreviation: 'NH'
      },
      {
        name: 'New Jersey',
        abbreviation: 'NJ'
      },
      {
        name: 'New Mexico',
        abbreviation: 'NM'
      },
      {
        name: 'New York',
        abbreviation: 'NY'
      },
      {
        name: 'North Carolina',
        abbreviation: 'NC'
      },
      {
        name: 'North Dakota',
        abbreviation: 'ND'
      },
      {
        name: 'Northern Mariana Islands',
        abbreviation: 'MP'
      },
      {
        name: 'Ohio',
        abbreviation: 'OH'
      },
      {
        name: 'Oklahoma',
        abbreviation: 'OK'
      },
      {
        name: 'Oregon',
        abbreviation: 'OR'
      },
      {
        name: 'Palau',
        abbreviation: 'PW'
      },
      {
        name: 'Pennsylvania',
        abbreviation: 'PA'
      },
      {
        name: 'Puerto Rico',
        abbreviation: 'PR'
      },
      {
        name: 'Rhode Island',
        abbreviation: 'RI'
      },
      {
        name: 'South Carolina',
        abbreviation: 'SC'
      },
      {
        name: 'South Dakota',
        abbreviation: 'SD'
      },
      {
        name: 'Tennessee',
        abbreviation: 'TN'
      },
      {
        name: 'Texas',
        abbreviation: 'TX'
      },
      {
        name: 'Utah',
        abbreviation: 'UT'
      },
      {
        name: 'Vermont',
        abbreviation: 'VT'
      },
      {
        name: 'Virgin Islands',
        abbreviation: 'VI'
      },
      {
        name: 'Virginia',
        abbreviation: 'VA'
      },
      {
        name: 'Washington',
        abbreviation: 'WA'
      },
      {
        name: 'West Virginia',
        abbreviation: 'WV'
      },
      {
        name: 'Wisconsin',
        abbreviation: 'WI'
      },
      {
        name: 'Wyoming',
        abbreviation: 'WY'
      }
    ]
  },

  // Canada
  {
    name: 'Canada',
    abbreviation: 'CA',
    regions: [
      {
        name: 'Alberta',
        abbreviation: 'AB'
      },
      {
        name: 'British Columbia',
        abbreviation: 'BC'
      },
      {
        name: 'Manitoba',
        abbreviation: 'MB'
      },
      {
        name: 'New Brunswick',
        abbreviation: 'NB'
      },
      {
        name: 'Newfoundland and Labrador',
        abbreviation: 'NL'
      },
      {
        name: 'Nova Scotia',
        abbreviation: 'NS'
      },
      {
        name: 'Northwest Territories',
        abbreviation: 'NT'
      },
      {
        name: 'Nunavut',
        abbreviation: 'NU'
      },
      {
        name: 'Ontario',
        abbreviation: 'ON'
      },
      {
        name: 'Prince Edward Island',
        abbreviation: 'PE'
      },
      {
        name: 'Quebec',
        abbreviation: 'QC'
      },
      {
        name: 'Saskatchewan',
        abbreviation: 'SK'
      },
      {
        name: 'Yukon',
        abbreviation: 'YT'
      }
    ]
  },
  {
    name: 'United Kingdom',
    abbreviation: 'GB',
    regions: []
  },
  {
    name: 'Spain',
    abbreviation: 'ES',
    regions: []
  },
  {
    name: 'Italy',
    abbreviation: 'IT',
    regions: []
  },
  {
    name: 'Belgium',
    abbreviation: 'BE',
    regions: []
  },
  {
    name: 'Germany',
    abbreviation: 'DE',
    regions: []
  },
  {
    name: 'France',
    abbreviation: 'FR',
    regions: []
  }
];

export function getCountryName(countryCode: string): string {
  return Countries.find(c => c.abbreviation === countryCode?.toUpperCase())?.name;
}

export const US_REGIONS_CODES = ['AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE',
  'DC', 'FM', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY',
  'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV',
  'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'MP', 'OH', 'OK', 'OR', 'PW', 'PA',
  'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VI', 'VA', 'WA', 'WV',
  'WI', 'WY'];

export const CA_REGIONS_CODES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON',
  'PE', 'QC', 'SK', 'YT'];

export function getRegions(countryAbbreviation: string) {
  if (isNilOrEmpty(countryAbbreviation)) {
    return [];
  }
  const country = Countries.find(c => c.abbreviation.toUpperCase() === (countryAbbreviation || '').toUpperCase());
  if (country === undefined) {
    throw new Error(`Invalid Country ${countryAbbreviation}`);
  }
  return country.regions.map(r => r);
}

export function getCountryForRegion(regionCode: string) {
  if (US_REGIONS_CODES.includes(regionCode?.toUpperCase())) {
    return 'US';
  }
  if (CA_REGIONS_CODES.includes(regionCode?.toUpperCase())) {
    return 'CA';
  }

  return null;
}
